import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart, faHeartCrack, faXmark } from "@fortawesome/free-solid-svg-icons";

// The scrollable list of recently-played rows (no overlay chrome). Purely
// presentational — the parent supplies the data (via useRecentlyPlayed) and the
// preference setter. Used both inside the overlay popup and inline in the
// expanded desktop panel.
//
//   tracks         — [{ track_id, title, artist, cover, played_at }]
//   currentTrackId — the live track, filtered out of the list
//   fallbackCover  — shown when a row has no stored cover
//   onSetPreference(trackId, "like" | "dislike" | null)
export const RecentlyPlayedList = ({
  tracks = [],
  loading = false,
  error = null,
  currentTrackId = null,
  fallbackCover = "",
  onSetPreference = () => {},
}) => {
  // Local optimistic state per row, keyed by track_id.
  const [prefs, setPrefs] = useState({});

  const visible = tracks.filter(
    (t) => String(t.track_id) !== String(currentTrackId)
  );

  const apply = (trackId, next) => {
    setPrefs((p) => ({ ...p, [trackId]: next }));
    onSetPreference(trackId, next);
  };

  return (
    <div className="recently-played-list">
      {loading && <div className="recently-played-empty">Loading…</div>}

      {error && !loading && (
        <div className="recently-played-empty">Couldn’t load recent tracks.</div>
      )}

      {!loading && !error && visible.length === 0 && (
        <div className="recently-played-empty">Nothing played yet.</div>
      )}

      {!loading &&
        !error &&
        visible.map((t) => {
          const pref = prefs[t.track_id] || null;
          return (
            <div className="recently-played-row" key={`${t.track_id}-${t.played_at}`}>
              <img
                className="recently-played-art"
                src={t.cover || fallbackCover}
                alt=""
                onError={(e) => {
                  if (fallbackCover && e.target.src !== fallbackCover) {
                    e.target.src = fallbackCover;
                  }
                }}
              />
              <div className="recently-played-meta">
                <span className="recently-played-title">{t.title}</span>
                <span className="recently-played-artist">{t.artist}</span>
              </div>
              <div className="recently-played-actions">
                <button
                  className={`heart ${pref === "like" ? "active" : ""}`}
                  onClick={() => apply(t.track_id, pref === "like" ? null : "like")}
                  aria-label="Like"
                >
                  <FontAwesomeIcon icon={faHeart} />
                </button>
                <button
                  className={`heartcrack ${pref === "dislike" ? "active" : ""}`}
                  onClick={() => apply(t.track_id, pref === "dislike" ? null : "dislike")}
                  aria-label="Dislike"
                >
                  <FontAwesomeIcon icon={faHeartCrack} />
                </button>
              </div>
            </div>
          );
        })}
    </div>
  );
};

// Popup overlay wrapping RecentlyPlayedList with a header + close button.
// Renders as an overlay so it works in any player view regardless of layout
// size. onClose closes the popup.
export const RecentlyPlayed = ({ onClose = () => {}, ...listProps }) => {
  return (
    <div className="recently-played-overlay" onClick={onClose}>
      <div
        className="recently-played-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Recently played"
      >
        <div className="recently-played-header">
          <span>Recently Played</span>
          <button
            className="recently-played-close"
            onClick={onClose}
            aria-label="Close recently played"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <RecentlyPlayedList {...listProps} />
      </div>
    </div>
  );
};

export default RecentlyPlayed;
